import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check } from 'lucide-react';
import PropTypes from 'prop-types';
import { API_URLS } from '../../../constants/api';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const ProsecutorsDropdown = ({
  isOpen,
  onClose,
  selectedProsecutors = [],
  onProsecutorsChange,
  buttonRef
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchInputRef = useRef(null);

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 320, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  // Fetch prosecutors from API based on search term
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const apiRequest = `${API_URLS.SEARCH_PROSECUTORS}?searchQuery=${encodeURIComponent(searchTerm)}`;
        const response = await fetch(apiRequest);
        const data = await response.json();
        setSuggestions(data.slice(0, 10));
      } catch (error) {
        console.error('Error searching prosecutors:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle prosecutor selection
  const handleProsecutorSelect = (prosecutor) => {
    const isSelected = selectedProsecutors.find(
      (p) =>
        p.firstName === prosecutor.firstName &&
        p.lastName === prosecutor.lastName
    );
    if (isSelected) {
      onProsecutorsChange(
        selectedProsecutors.filter(
          (p) =>
            !(
              p.firstName === prosecutor.firstName &&
              p.lastName === prosecutor.lastName
            )
        )
      );
    } else {
      onProsecutorsChange([...selectedProsecutors, prosecutor]);
    }
    setSearchTerm('');
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          handleProsecutorSelect(suggestions[focusedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onProsecutorsChange([]);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-80 sm:w-80'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '320px' // Ensure minimum usable width
              }
        }
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Select Prosecutors
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search prosecutors..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Selected Prosecutors */}
          {selectedProsecutors.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Selected ({selectedProsecutors.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedProsecutors.map((prosecutor, index) => (
                  <span
                    key={`${prosecutor.firstName}-${prosecutor.lastName}-${index}`}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {prosecutor.firstName} {prosecutor.lastName}
                    <button
                      onClick={() => handleProsecutorSelect(prosecutor)}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Prosecutors List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <div className="mt-2">Searching prosecutors...</div>
              </div>
            ) : suggestions.length > 0 ? (
              <div className="space-y-1">
                {suggestions.map((prosecutor, index) => {
                  const isSelected = selectedProsecutors.find(
                    (p) =>
                      p.firstName === prosecutor.firstName &&
                      p.lastName === prosecutor.lastName
                  );
                  const isFocused = focusedIndex === index;
                  return (
                    <button
                      key={`${prosecutor.firstName}-${prosecutor.lastName}-${index}`}
                      onClick={() => handleProsecutorSelect(prosecutor)}
                      className={`w-full p-2 text-left text-sm border rounded-md transition-colors ${
                        isSelected
                          ? 'bg-blue-50 border-blue-200 text-blue-900'
                          : isFocused
                            ? 'bg-gray-50 border-gray-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      <div className="flex items-center justify-between">
                        <span>
                          {prosecutor.firstName} {prosecutor.lastName}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                {searchTerm.length >= 3
                  ? `No prosecutors found for "${searchTerm}"`
                  : searchTerm.length > 0
                    ? 'Type at least 3 characters to search'
                    : 'Search for prosecutors by typing their name'}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-4">
            <button
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={selectedProsecutors.length === 0}
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

ProsecutorsDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedProsecutors: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired
    })
  ),
  onProsecutorsChange: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default ProsecutorsDropdown;
